import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import norecords from "../../../assests/norecords.gif";
import "../../../App.css";
import CustomButton from "../../Buttons/CustomButton";
import DeleteConfirmationModal from "../../migrations/DeleteConfirmationModal";
import { showToast } from "../../Buttons/Toaster";

const cellStyle = {
  textAlign: "left",
  verticalAlign: "middle",
};

function UserManagement({ 
  users: initialUsers, 
  onEdit, 
  onAdd, 
  onRefresh,
  selectedCount,
  setSelectedCount,
  isDeleteEnabled, 
  setIsDeleteEnabled,
  checkedRows,
  setCheckedRows
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Remove local modal states - let parent handle them
  const headerCheckboxRef = useRef(null);

  // Update users when initialUsers prop changes
  useEffect(() => {
    if (Array.isArray(initialUsers)) {
      setUsers(initialUsers);
      setLoading(false);
    } else {
      setUsers([]);
      // Only fetch if no initial users provided
      if (!initialUsers) {
        fetchUsers();
      }
    }
  }, [initialUsers]);

  const fetchUsers = async () => {
    const apiEndUsers = process.env.REACT_APP_API_URL_CODE_CONVERSION;

    try {
      setLoading(true);
      const access_token = sessionStorage.getItem("access_token");

      if (!access_token) {
        throw new Error('No access token found');
      }
      
      const response = await fetch(`${apiEndUsers}/get_users`, {
        method: "GET",
        headers: {
          "accept": "application/json",
          "Authorization": `Bearer ${access_token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching users:", err);
      showToast(`Error fetching users: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Remove handleConfirmDelete - parent will handle deletion
  // Remove modal states and handlers - parent will handle them

  const filteredData = users.filter((row) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (row.full_name && row.full_name.toLowerCase().includes(query)) ||
      (row.username && row.username.toLowerCase().includes(query)) ||
      (row.role && row.role.toLowerCase().includes(query)) ||
      (row.email && row.email.toLowerCase().includes(query))
    );
  });

  // Update header checkbox state
  useEffect(() => {
    if (headerCheckboxRef.current) {
      const allChecked = filteredData.length > 0 &&
        filteredData.every(row => checkedRows[row.user_id]);
      const someChecked = filteredData.some(row => checkedRows[row.user_id]) && !allChecked;

      headerCheckboxRef.current.indeterminate = someChecked;
      headerCheckboxRef.current.checked = allChecked;
    }

    setIsDeleteEnabled(selectedCount > 0);
  }, [filteredData, checkedRows, selectedCount, setIsDeleteEnabled]);

  const handleCheckboxChange = (id) => {
    setCheckedRows(prev => {
      const newChecked = {
        ...prev,
        [id]: !prev[id]
      };
      const count = Object.values(newChecked).filter(Boolean).length;
      setSelectedCount(count);
      setIsDeleteEnabled(count > 0);
      return newChecked;
    });
  };

  const handleSelectAllChange = useCallback(() => {
    const allChecked = filteredData.length > 0 &&
      filteredData.every(row => checkedRows[row.user_id]);

    setCheckedRows(prev => {
      const newChecked = { ...prev };
      if (allChecked) {
        // Uncheck all
        filteredData.forEach(row => {
          delete newChecked[row.user_id];
        });
        setSelectedCount(0);
        setIsDeleteEnabled(false);
      } else {
        // Check all
        filteredData.forEach(row => {
          newChecked[row.user_id] = true;
        });
        setSelectedCount(filteredData.length);
        setIsDeleteEnabled(filteredData.length > 0);
      }
      return newChecked;
    });
  }, [filteredData, checkedRows, setSelectedCount, setIsDeleteEnabled, setCheckedRows]);

  const TruncatedText = ({ text, maxLength = 28 }) => {
    if (!text) return "N/A";
    const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    return <span title={text.length > maxLength ? text : null}>{truncated}</span>;
  };

  const TextSkeleton = ({ width = '100%', height = '16px', className = '', style = {} }) => (
    <div
      className={className}
      style={{
        width,
        height,
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        borderRadius: "4px",
        animation: "pulse 1.5s infinite",
        ...style
      }}
    />
  );

  if (error) {
    return (
      <div className="migration-table-container" style={{marginTop:"-70px"}}>
        <div className="page-content">
          <div className="container-fluid">
            <div className="text-center py-5 text-danger">Error loading users: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="migration-table-container" style={{marginTop:"-70px"}}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.3; }
            100% { opacity: 0.6; }
          }
        `}
      </style>
      <div className="page-content userfixed">
        <div className="container-fluid" style={{marginTop:"-5px"}}>
          {/* Header with Search */}
          <div className="d-flex justify-content-between align-items-center mb-2 userfixed">
            <div>
              {loading ? (
                <>
                  <TextSkeleton width="80px" height="24px" className="mb-2" />
                  <div className="d-flex align-items-center gap-2">
                    <TextSkeleton width="60px" height="14px" />
                    <TextSkeleton width="20px" height="14px" />
                    <TextSkeleton width="100px" height="14px" />
                  </div>
                </>
              ) : (
                <div className="search-bar my-2">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control highlighted-input colorfor-placeholder styled-input font-size-12 doc-search text-d-search pr-5"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-1">
            {loading ? (
              <TextSkeleton
                width="140px"
                height="29px"
                style={{
                  borderRadius: "8px 8px 0 0",
                  backgroundColor: "#dcdfe3"
                }}
              />
            ) : (
              <ul className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content" role="tablist">
                <li className="nav-item" role="presentation">
                  <a
                    className="nav-link active w-fit-content"
                    data-bs-toggle="tab"
                    href="#migrationjobs"
                    role="tab"
                    aria-selected="true"
                    style={{
                      backgroundColor: "#dcdfe3",
                      borderRadius: "8px 8px 0 0",
                      padding: "10px 20px",
                      width: "140px",
                      height: "29px",
                      fontFamily: "Inter",
                      fontWeight: 700,
                      fontSize: "12px",
                      lineHeight: "21px",
                      letterSpacing: "0%",
                      color: "#354258",
                      position: "relative",
                      zIndex: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <span className="d-sm-block white-space-nowrap">
                      User Management
                      <span
                        style={{
                          display: "block",
                          height: "1px",
                          backgroundColor: "#354258",
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          zIndex: 4,
                        }}
                      />
                    </span>
                  </a>
                </li>
              </ul>
            )}
          </div>

          {/* Table Content */}
          <div className="tab-content tab-con">
            <div
              className="tab-pane active"
              id="userManagement"
              role="tabpanel"
              style={{
                border: "0.5px solid rgb(255 250 250 / 20%)",
                borderLeft:"0px",
                borderRight:"0px",
                borderTop: "none",
                borderRadius: "0 0 4px 4px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "50.2vh",
              }}
            >
              {/* Fixed Header Container */}
              <div style={{ overflow: "hidden", flexShrink: 0 }}>
                <table className="table mb-0">
                  <thead className="table-head fw-bold">
                    <tr>
                      <th style={{ ...cellStyle, width: "5%", fontSize: "11px", paddingLeft: "15px" }}>
                        {!loading && (
                          <input
                            type="checkbox"
                            ref={headerCheckboxRef}
                            className="custom-checkbox mb-0"
                            style={{ width: "17px" }}
                            onChange={handleSelectAllChange}
                            aria-label={filteredData.every(row => checkedRows[row.user_id]) ?
                              "Deselect all" : "Select all"}
                          />
                        )}
                      </th>
                      <th style={{ ...cellStyle, width: "25%", fontSize: "11px" }}>Name</th>
                      <th style={{ ...cellStyle, width: "20%", fontSize: "11px" }}>Username</th>
                      <th style={{ ...cellStyle, width: "30%", fontSize: "11px" }}>Email</th>
                      <th style={{ ...cellStyle, width: "20%", fontSize: "11px" }}>Role</th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Scrollable Body Container */}
              <div style={{ overflowY: "auto", flexGrow: 1 }}>
                <table className="table mb-0">
                  <tbody className="scrollable-tbody" style={{ zIndex: 1 }}>
                    {loading ? (
                      Array(5).fill(0).map((_, index) => (
                        <tr key={`skeleton-${index}`}>
                          <td style={{ ...cellStyle, width: "5%", paddingLeft: "15px" }}>
                            <TextSkeleton width="17px" height="17px" />
                          </td>
                          <td style={{ ...cellStyle, width: "25%" }}>
                            <TextSkeleton width="80%" height="16px" />
                          </td>
                          <td style={{ ...cellStyle, width: "20%" }}>
                            <TextSkeleton width="70%" height="16px" style={{ animationDelay: "0.2s" }} />
                          </td>
                          <td style={{ ...cellStyle, width: "30%" }}>
                            <TextSkeleton width="90%" height="16px" style={{ animationDelay: "0.4s" }} />
                          </td>
                          <td style={{ ...cellStyle, width: "20%" }}>
                            <TextSkeleton width="60%" height="16px" style={{ animationDelay: "0.6s" }} />
                          </td>
                        </tr>
                      ))
                    ) : filteredData.length > 0 ? (
                      filteredData.map((row) => (
                        <tr
                          key={row.user_id}
                          style={{
                            ...cellStyle,
                            backgroundColor: checkedRows[row.user_id] ? "#F1F3FB" : "transparent",
                          }}
                        >
                          <td style={{ ...cellStyle, width: "5%", paddingLeft: "15px" }}>
                            <input
                              type="checkbox"
                              checked={checkedRows[row.user_id] || false}
                              className="custom-checkbox mr-2"
                              onChange={() => handleCheckboxChange(row.user_id)}
                              style={{ width: "17px" }}
                              aria-label={`Select ${row.full_name}`}
                            />
                          </td>
                          <td style={{ ...cellStyle, width: "25%" }}>
                            <TruncatedText text={row.full_name} />
                          </td>
                          <td style={{ ...cellStyle, width: "20%" }}>{row.username || "N/A"}</td>
                          <td style={{ ...cellStyle, width: "30%" }}>{row.email || "N/A"}</td>
                          <td style={{ ...cellStyle, width: "20%" }}>{row.role || "N/A"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center">
                          <img src={norecords || "/placeholder.svg"} alt="No Records" style={{ width: "203px" }} />
                          <h4 className="fw-bold pt-3" style={{ fontSize: "18px" }}>
                            No users found
                          </h4>
                          <p>Looks like there are no users yet.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;